// Workflows for one project. Every rule is one row that reads as a sentence:
// when THIS happens, do these steps, stop when that. The row is the whole
// summary — you should never have to open a rule to find out what it does.

import React, { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, History, Workflow, CircleSlash } from "lucide-react";
import { Button } from "../../../atoms";
import ConfirmDeleteModal from "../../../overlays/ConfirmDeleteModal";
import HistoryPanel from "./HistoryPanel";
import WorkflowEditor from "./WorkflowEditor";
import { TriggerChip, ActionChip, StepGap, Toggle, iconBtn } from "./chips";
import {
  WORKFLOWS, HISTORY, makeRule, totalDays, describeStops, summariseHistory,
} from "./workflowRules.mjs";

function Meta({ rule }) {
  const days = totalDays(rule.steps);
  const sum = summariseHistory(HISTORY[rule.id] || []);
  const bits = [
    `${rule.steps.length} ${rule.steps.length === 1 ? "step" : "steps"}`,
    days ? `over ${days} ${days === 1 ? "day" : "days"}` : "same day",
  ];
  if (sum.leads) bits.push(`${sum.leads} ${sum.leads === 1 ? "lead" : "leads"}`);
  if (sum.wins) bits.push(`${sum.wins} came back`);
  return (
    <span style={{ fontSize: 11.5, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
      {bits.join(" · ")}
    </span>
  );
}

function RuleRow({ rule, onToggle, onEdit, onHistory, onDelete, last }) {
  const waiting = summariseHistory(HISTORY[rule.id] || []).waiting;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
        borderBottom: last ? "none" : "1px solid var(--color-border-subtle)",
        opacity: rule.enabled ? 1 : 0.72,
      }}
    >
      {/* the sentence */}
      <button
        type="button"
        onClick={onEdit}
        className="tg-filter-opt"
        title="Edit this workflow"
        style={{
          flex: 1, minWidth: 0, display: "block", textAlign: "left",
          border: "none", cursor: "pointer", padding: 0, borderRadius: 6,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
            {rule.name}
          </span>
          {waiting > 0 && (
            <span style={{ fontSize: 11, color: "var(--crm-warning)", fontWeight: 500 }}>
              {waiting} waiting on you
            </span>
          )}
        </span>

        <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
          <TriggerChip trigger={rule.trigger} />
          <span aria-hidden="true" style={{ color: "var(--color-text-muted)", fontSize: 11, padding: "0 1px" }}>then</span>
          {rule.steps.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && <StepGap waitDays={s.waitDays} />}
              <ActionChip action={s.action} title={s.text} />
            </React.Fragment>
          ))}
        </span>

        <span style={{ display: "block", marginTop: 5, fontSize: 11.5, color: "var(--color-text-secondary)" }}>
          Stops when: {describeStops(rule.stops)}
        </span>
      </button>

      {/* state + actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <Meta rule={rule} />
        <Toggle on={rule.enabled} onChange={onToggle} label={`Turn ${rule.name} ${rule.enabled ? "off" : "on"}`} />
        <button type="button" title="History" aria-label={`History for ${rule.name}`} onClick={onHistory} style={iconBtn}>
          <History size={13} />
        </button>
        <button type="button" title="Edit" aria-label={`Edit ${rule.name}`} onClick={onEdit} style={iconBtn}>
          <Pencil size={13} />
        </button>
        <button
          type="button" title="Delete" aria-label={`Delete ${rule.name}`} onClick={onDelete}
          style={{ ...iconBtn, color: "var(--crm-danger)" }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

export default function WorkflowsPanel() {
  // Local state, because there is no workflow API yet. Editing behaves exactly
  // as it will once there is one, so the interaction can be judged now.
  const [rules, setRules] = useState(WORKFLOWS);
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const live = useMemo(() => rules.filter((r) => r.enabled).length, [rules]);

  const patch = (id, next) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...next } : r)));
  const save = (rule) =>
    setRules((rs) => (rs.some((r) => r.id === rule.id) ? rs.map((r) => (r.id === rule.id ? rule : r)) : [...rs, rule]));

  return (
    <section style={{
      background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8,
    }}>
      <header style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Workflow size={14} style={{ color: "var(--crm-primary)" }} />
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Workflows</h3>
            <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>
              {live} of {rules.length} on
            </span>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: "18px" }}>
            When something happens to a lead in this project, do this automatically. Sample data — nothing sends yet.
          </p>
        </div>
        <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => setEditing(makeRule())}>
          New workflow
        </Button>
      </header>

      {rules.length === 0 ? (
        <div style={{ padding: "30px 20px", textAlign: "center" }}>
          <CircleSlash size={20} style={{ color: "var(--color-text-muted)" }} />
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 8 }}>No workflows on this project</div>
          <p style={{ margin: "4px auto 12px", maxWidth: 340, fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: "18px" }}>
            Start with the obvious one: when a lead is added, send a WhatsApp and an email.
          </p>
          <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => setEditing(makeRule())}>
            New workflow
          </Button>
        </div>
      ) : (
        rules.map((r, i) => (
          <RuleRow
            key={r.id}
            rule={r}
            last={i === rules.length - 1}
            onToggle={(on) => patch(r.id, { enabled: on })}
            onEdit={() => setEditing(r)}
            onHistory={() => setHistoryOf(r)}
            onDelete={() => setConfirm(r)}
          />
        ))
      )}

      <WorkflowEditor
        rule={editing}
        onClose={() => setEditing(null)}
        onSave={(rule) => { save(rule); setEditing(null); }}
      />

      <HistoryPanel
        open={!!historyOf}
        onClose={() => setHistoryOf(null)}
        workflow={historyOf}
        runs={historyOf ? HISTORY[historyOf.id] || [] : []}
      />

      <ConfirmDeleteModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { setRules((rs) => rs.filter((r) => r.id !== confirm.id)); setConfirm(null); }}
        title="Delete this workflow?"
        message={confirm ? `"${confirm.name}" will stop running. Leads already part-way through it are left where they are.` : ""}
        confirmLabel="Delete workflow"
      />
    </section>
  );
}
